import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { ClinicOwnerUpdatePurchasedSubscriptionDto } from '@docbay/schemas';

import {
  ClinicOwnerActions,
  ClinicOwnerSubscriptionActions,
} from 'applicaiton/constants/actions';

import { ClinicOwnerSubscriptionAPI } from 'integration/api/clinicOwnerSubscription';

/**
 * Async thunk to get the current subscription.
 *
 * @returns The data of the current subscription if successful,
 * or an error message if unsuccessful.
 */
export const getCurrentSubscription = createAsyncThunk(
  ClinicOwnerSubscriptionActions.getCurrentSubscription,
  async (_, { rejectWithValue }) => {
    try {
      const { data } =
        await ClinicOwnerSubscriptionAPI.getCurrentSubscription();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk to update the current subscription.
 *
 * @param params - The parameters for updating the purchased subscription.
 * @returns The data of the updated subscription if successful,
 * or an error message if unsuccessful.
 */
export const updateCurrentSubscription = createAsyncThunk(
  ClinicOwnerSubscriptionActions.updateCurrentSubscription,
  async (
    params: ClinicOwnerUpdatePurchasedSubscriptionDto,
    { rejectWithValue },
  ) => {
    try {
      const { data } =
        await ClinicOwnerSubscriptionAPI.updateCurrentSubscription(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk to get the payment methods.
 *
 * @returns The data of the payment methods if successful,
 * or an error message if unsuccessful.
 */
export const getPaymentMethods = createAsyncThunk(
  ClinicOwnerSubscriptionActions.getPaymentMethods,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerSubscriptionAPI.getPaymentMethods();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk to set the default payment method.
 *
 * @param paymentMethodId - The ID of the payment method to set as default.
 * @returns The data of the updated default payment method if successful,
 * or an error message if unsuccessful.
 */
export const setDefaultPaymentMethod = createAsyncThunk(
  ClinicOwnerSubscriptionActions.setDefaultPaymentMethod,
  async (paymentMethodId: string, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerSubscriptionAPI.setDefaultPaymentMethod(
        paymentMethodId,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

export const getClinicOwnerSubscriptionsDetails = createAsyncThunk(
  ClinicOwnerSubscriptionActions.getClinicOwnerSubscriptionsDetails,
  async (token: string, { rejectWithValue }) => {
    try {
      const { data } =
        await ClinicOwnerSubscriptionAPI.getClinicOwnerSubscriptionsDetails(
          token,
        );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk cancel Subscription plan.
 *
 */
export const cancelSubscriptionPlan = createAsyncThunk(
  ClinicOwnerActions.cancelSubscriptionPlan,
  async (_, { rejectWithValue }) => {
    try {
      const { data } =
        await ClinicOwnerSubscriptionAPI.cancelSubscriptionPlan();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
